import {
	CairoBlack,
	CairoBold,
	CairoExtraBold,
	CairoExtraLight,
	CairoLight,
	CairoMedium,
	CairoRegular,
	CairoSemiBold,
} from '@Assets/Fonts/Cairo'
import {
	MontserratBlack,
	MontserratBold,
	MontserratLight,
	MontserratNormal,
	MontserratMedium,
	MontserratThin,
	MontserratExtraLight,
	MontserratSemiBold,
	MontserratExtraBold,
} from '@Assets/Fonts/Montserrat'
import { MulishBold, MulishLight, MulishRegular } from '@Assets/Fonts/Mulish'
import { AvantGardeRegular } from '@Assets/Fonts/AvantGarde'
import { DubaiBold, DubaiMedium, DubaiRegular } from '@Assets/Fonts/Dubai'
import { rgba, normalize, darken } from 'polished'
import { createGlobalStyle } from 'styled-components'
import { FawryStyles } from './FawryStyles'
import { OpayStyles } from './OpayStyles'
import {
	MaterialIconsOutlined,
	MaterialIconsRegular,
	MaterialIconsRound,
	MaterialIconsSharp,
	MaterialIconsTwoTone,
} from '@Assets/Fonts/MaterialIcons'
import { FAQsTable } from '@eduact/ed-system'
import { createGlobalFont, Fonts, generateMaterialIconsFontFaces, MaterialIconFontFace } from '@eduact/ed-system'
import { RobotoBlack, RobotoBold, RobotoLight, RobotoMedium, RobotoRegular, RobotoThin } from '@Assets/Fonts/Roboto'
// `Fonts` is the shared theme's map and does not list Dubai, the AbwaabPlus
// brand typeface added by this fork. The extra key is intentional and the
// weights below are real; only the shared type is behind.
const fontWeightsSrc = {
	fonts: {
		Montserrat: {
			normal: MontserratNormal,
			bold: MontserratBold,
			bolder: MontserratBlack,
			light: MontserratLight,
			'500': MontserratMedium,
			'100': MontserratThin,
			'200': MontserratExtraLight,
			'300': MontserratLight,
			'400': MontserratNormal,
			'600': MontserratSemiBold,
			'700': MontserratBold,
			'800': MontserratExtraBold,
			'900': MontserratBlack,
		},
		Mulish: {
			bold: MulishBold,
			normal: MulishRegular,
			'300': MulishLight,
		},
		Cairo: {
			normal: CairoRegular,
			bold: CairoBold,
			bolder: CairoBlack,
			light: CairoLight,
			'200': CairoExtraLight,
			'300': CairoLight,
			'400': CairoRegular,
			'500': CairoMedium,
			'600': CairoSemiBold,
			'700': CairoBold,
			'800': CairoExtraBold,
			'900': CairoBlack,
		},
		AvantGarde: {
			normal: AvantGardeRegular,
		},
		// AbwaabPlus brand typeface (Dubai). Shipped weights: Regular 400 / Medium 500 /
		// Bold 700. Light 300 maps to Regular; 600 ("semibold") maps to Bold by design.
		Dubai: {
			normal: DubaiRegular,
			light: DubaiRegular,
			'300': DubaiRegular,
			'400': DubaiRegular,
			'500': DubaiMedium,
			'600': DubaiBold,
			'700': DubaiBold,
			bold: DubaiBold,
		},
		Roboto: {
			'100': RobotoThin,
			'300': RobotoLight,
			'400': RobotoRegular,
			normal: RobotoRegular,
			'500': RobotoMedium,
			'700': RobotoBold,
			bold: RobotoBold,
			'900': RobotoBlack,
			bolder: RobotoBlack,
		},
	},
}

const materialFonts: MaterialIconFontFace = {
	Outlined: { src: MaterialIconsOutlined },
	Regular: { src: MaterialIconsRegular, mapped_name: '' },
	Round: { src: MaterialIconsRound },
	Sharp: { src: MaterialIconsSharp },
	TwoTone: { src: MaterialIconsTwoTone, mapped_name: 'TwoTone' },
}

/**
 * The AbwaabPlus design-system theme maps the brand blue onto `colors.primary`.
 * We use that as a stable marker to decide typography + token derivation without
 * adding a non-standard field to the `ITheme` shape.
 */
const ABWAAB_PLUS_PRIMARY = '#0655CB'
const isAbwaabPlus = (theme: { colors: { primary: string } }) =>
	theme.colors.primary?.toUpperCase() === ABWAAB_PLUS_PRIMARY

const GlobalStyles = createGlobalStyle`
${normalize()}

* {
  /* outline: ${process.env.NODE_ENV === 'development' ? ' 1px solid tomato' : ''}; */
}


iframe {
	/* pointer-events: ${process.env.NODE_ENV === 'development' ? '' : 'none'}; */
}
	${generateMaterialIconsFontFaces(materialFonts)}
	${createGlobalFont('Roboto', fontWeightsSrc)};
	${createGlobalFont('Montserrat', fontWeightsSrc)};
	${createGlobalFont('Mulish', fontWeightsSrc)};
	${createGlobalFont('Cairo', fontWeightsSrc)};
	${createGlobalFont('AvantGarde', fontWeightsSrc)};
	/* Cast: Dubai is the AbwaabPlus brand typeface and is shipped above, but
	   the shared theme's FontFamily union does not list it. */
	${createGlobalFont('Dubai' as any, fontWeightsSrc)};

  * {
    box-sizing: border-box;
	
	}

  html ,body , #root{
	  padding : 0;
	  margin : 0;

	img {
		max-width: 100%;
	}
}
  /* styled-reset (mounted after GlobalStyles) applies \`font: inherit\` to html/body,
     which strips the base font and serifs Arabic in body-level portals (modals,
     drawers, snackbars). Form controls also don't inherit font-family by default
     (and the legacy stack forces Roboto on inputs → non-Dubai placeholders).
     Re-assert the brand font with !important under AbwaabPlus. */
  ${({ theme }) =>
		isAbwaabPlus(theme)
			? `html, body, #root, input, textarea, select, button, ::placeholder { font-family: 'Dubai', 'Cairo', system-ui, sans-serif !important; }`
			: ''}
html ,body ,#root{
	  font-family: ${({ theme }) => (isAbwaabPlus(theme) ? "'Dubai', 'Cairo'" : "'Montserrat'")};
	  ::-webkit-scrollbar {
		appearance: none;
		width : 5px;
	  }
	  ::-webkit-scrollbar-track{
		border-radius : 10px;
		background: ${props => props.theme.colors.silver}
	}
	::-webkit-scrollbar-thumb{
		border-radius : 10px;
		  background: ${props => props.theme.colors.spanishGray}
	  }
  }
  * {
  ::-webkit-scrollbar {
		appearance: none;
		width : 5px;
	  }
	  ::-webkit-scrollbar-track{
		border-radius : 10px;
		background: ${props => props.theme.colors.silver}
	}
	::-webkit-scrollbar-thumb{
		border-radius : 10px;
		  background: ${props => props.theme.colors.spanishGray}
	  }
  }
  html {
	  overflow-x :hidden;
	  height : 100%;
  }
  body , #root{
	min-height :100%;
  }
  ${({ theme }) => (isAbwaabPlus(theme) ? `body { background: var(--surface-page); color: var(--text-primary); }` : '')}
  html[dir="rtl"],body[dir="rtl"]{
	  font-family : ${({ theme }) => (isAbwaabPlus(theme) ? "'Dubai', 'Cairo'" : "'Cairo'")};

  }
  html {
  box-sizing: border-box;
}
*, *:before, *:after {
  box-sizing: inherit;
}
 
  
  ${FawryStyles}
  ${OpayStyles}
  ${FAQsTable}

  :root {
	${({ theme }) => `${theme.mediaQueries.min(1366)}{
		font-size :14px;
	}`}
  ${({ theme }) => `${theme.mediaQueries.min(1440)}{
		font-size :15px;
	}`}
	 ${({ theme }) => `${theme.mediaQueries.xlarge}{
		font-size :15px;
	}`}
	${({ theme }) => `${theme.mediaQueries.min(1536)}{
		font-size :16px;
	}`}
}
:root {
	--app-header-height: 4.938rem;
}

/* ─────────────────────────────────────────────────────────────
   AbwaabPlus design-system token layer.
   Brand + signal colors are derived from the active theme so switching
   themes recolors the design-system primitives; neutrals, spacing, radii,
   type scale and motion are constants taken from the design-system tokens
   (abwaabplus-design-system/project/tokens/*.css).
   ───────────────────────────────────────────────────────────── */
:root {
	/* Brand — derived from the active theme */
	--blue: ${({ theme }) => theme.colors.primary};
	--blue-hover: ${({ theme }) => darken(0.1, theme.colors.primary)};
	--navy: ${({ theme }) => darken(0.18, theme.colors.primary)};
	--yellow: ${({ theme }) => theme.colors.yellow};
	--gold: #D9A901;
	--gold-deep: #B48C0E;

	/* Functional UI signals */
	--success: ${({ theme }) => theme.colors.green};
	--success-dark: #15803D;
	--success-light: #86EFAC;
	--error: ${({ theme }) => theme.colors.red};
	--error-dark: #B91C1C;
	--warning: ${({ theme }) => theme.colors.orange};
	--warning-dark: #B45309;
	--neutral: #9CA3AF;

	/* Surfaces */
	--surface-page: #F5F5F5;
	--surface-card: #FFFFFF;
	--surface-sunken: #F9FAFB;
	--surface-sunken-strong: #F3F4F6;

	/* Text */
	--text-primary: #1F2937;
	--text-secondary: #6B7280;
	--text-disabled: #9CA3AF;
	--text-on-brand: #FFFFFF;

	/* Lines & chrome */
	--border: #E5E7EB;
	--scrollbar-thumb: #D1D5DB;
	--scrim: rgba(0, 0, 0, 0.5);

	/* Tint ladder — derived from brand */
	--blue-08: ${({ theme }) => rgba(theme.colors.primary, 0.08)};
	--blue-10: ${({ theme }) => rgba(theme.colors.primary, 0.1)};
	--blue-12: ${({ theme }) => rgba(theme.colors.primary, 0.12)};
	--blue-20: ${({ theme }) => rgba(theme.colors.primary, 0.2)};
	--yellow-12: ${({ theme }) => rgba(theme.colors.yellow, 0.12)};
	--success-10: ${({ theme }) => rgba(theme.colors.green, 0.1)};
	--error-10: ${({ theme }) => rgba(theme.colors.red, 0.1)};
	--warning-10: ${({ theme }) => rgba(theme.colors.orange, 0.1)};

	/* Typography */
	--font-sans: ${({ theme }) => (isAbwaabPlus(theme) ? "'Dubai', 'Cairo', system-ui, sans-serif" : "'Cairo', 'Montserrat', system-ui, sans-serif")};
	--font-mono: ui-monospace, 'SFMono-Regular', Menlo, monospace;
	--fw-light: 300;
	--fw-regular: 400;
	--fw-medium: 500;
	--fw-bold: 700;
	--fs-micro: 10px;
	--fs-micro-plus: 11px;
	--fs-label: 12px;
	--fs-body: 14px;
	--fs-body-lg: 16px;
	--fs-title: 18px;
	--fs-title-lg: 20px;
	--fs-display: 24px;
	--lh-tight: 1.1;
	--lh-snug: 1.25;
	--lh-normal: 1.5;

	/* Spacing (4px base) */
	--space-0_5: 2px;
	--space-1: 4px;
	--space-1_5: 6px;
	--space-2: 8px;
	--space-3: 12px;
	--space-4: 16px;
	--space-5: 20px;
	--space-6: 24px;
	--space-8: 32px;
	--space-12: 48px;
	--space-20: 80px;

	/* Radius */
	--r-checkbox: 4px;
	--r-sm: 8px;
	--r-md: 10px;
	--r-lg: 12px;
	--r-xl: 16px;
	--r-2xl: 20px;
	--r-full: 9999px;

	/* Elevation */
	--sh-rest: 0 1px 2px rgba(0, 0, 0, 0.05);
	--sh-hover: 0 2px 6px rgba(0, 0, 0, 0.1);
	--sh-overlay: 0 8px 16px rgba(0, 0, 0, 0.15);
	--bw-hairline: 1px;

	/* Layout & sizing (student surface) */
	--container-max: 512px;
	--topbar-height: 56px;
	--bottomnav-height: 64px;
	--screen-pad-x: 16px;
	--screen-pad-y: 16px;
	--content-bottom-pad: 80px;
	--touch-min: 44px;
	--icon-chrome: 22px;
	--icon-inline: 18px;
	--icon-file: 16px;
	--icon-hero: 48px;
	--progress-track: 8px;
	--progress-track-thin: 6px;

	/* The one gradient in the system (course-detail header) */
	--gradient-course-header: ${({ theme }) => `linear-gradient(135deg, ${theme.colors.primary}, ${darken(0.18, theme.colors.primary)})`};

	/* Motion */
	--dur-fast: 150ms;
	--dur-base: 200ms;
	--dur-fill: 300ms;
	--dur-sheet-open: 500ms;
	--dur-sheet-close: 300ms;
	--ease: cubic-bezier(0.4, 0, 0.2, 1);
}

.command-palette{
	z-index: 1000;
}

/* WaselChat launcher, hidden without unmounting (the vendor ships no hide
   API, but the iframe lives in our DOM). Toggled by the WaselChat component
   on the exam player routes. */
html.waselchat-hidden #abwaabchat-frame {
	display: none !important;
}
  `
export default GlobalStyles
